import React, { useEffect, useMemo, useState } from 'react';
import { staffApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CheckCircle2, FileText, Loader2, Receipt, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const CHUNK_SIZE = 8;

// Caricamento massivo buste paga: analisi (dry-run) con match per codice
// fiscale letto dal PDF → conferma → upload definitivo nella cartella scelta.
export default function PayslipBulkDialog({ onClose, onDone }) {
  const [folders, setFolders] = useState([]);
  const [folderId, setFolderId] = useState('none');
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [files, setFiles] = useState([]);
  const [phase, setPhase] = useState('select'); // select | analyzing | review | uploading | done
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState(0);
  const [finalSummary, setFinalSummary] = useState(null);

  useEffect(() => {
    staffApi.listFolders().then(r => setFolders(r.data || [])).catch(() => {});
  }, []);

  const folderOptions = useMemo(
    () => [...folders].sort((a, b) => (a.path || '').localeCompare(b.path || '')),
    [folders]
  );

  const sendChunks = async (fileList, dryRun) => {
    const all = [];
    for (let i = 0; i < fileList.length; i += CHUNK_SIZE) {
      const chunk = fileList.slice(i, i + CHUNK_SIZE);
      const fd = new FormData();
      chunk.forEach(f => fd.append('files', f));
      fd.append('dry_run', dryRun ? 'true' : 'false');
      if (folderId !== 'none') fd.append('folder_id', folderId);
      if (year) fd.append('year', year);
      const r = await staffApi.bulkPayslips(fd);
      all.push(...(r.data?.results || []));
      setProgress(Math.min(i + CHUNK_SIZE, fileList.length));
    }
    return all;
  };

  const analyze = async () => {
    if (files.length === 0) { toast.error('Seleziona almeno un PDF'); return; }
    setPhase('analyzing'); setProgress(0);
    try {
      const res = await sendChunks(files, true);
      setResults(res);
      setPhase('review');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore analisi file');
      setPhase('select');
    }
  };

  const commit = async () => {
    const matchedNames = new Set(results.filter(r => r.status === 'matched').map(r => r.filename));
    const toUpload = files.filter(f => matchedNames.has(f.name));
    if (toUpload.length === 0) { toast.error('Nessuna busta paga riconosciuta da caricare'); return; }
    setPhase('uploading'); setProgress(0);
    try {
      const res = await sendChunks(toUpload, false);
      const ok = res.filter(r => r.status === 'matched').length;
      setFinalSummary({ assigned: ok, failed: res.length - ok });
      setPhase('done');
      toast.success(`${ok} bust${ok === 1 ? 'a' : 'e'} paga assegnat${ok === 1 ? 'a' : 'e'}`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento');
      setPhase('review');
    }
  };

  const matchedCount = results.filter(r => r.status === 'matched').length;
  const busy = phase === 'analyzing' || phase === 'uploading';

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="payslip-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5" /> Caricamento massivo buste paga
          </DialogTitle>
          <DialogDescription>
            Ogni PDF viene assegnato automaticamente al dipendente leggendo il codice fiscale dal documento.
            Le buste paga sono salvate come documenti <b>riservati</b> (visibili a OPS/HR e al dipendente).
          </DialogDescription>
        </DialogHeader>

        {phase === 'select' && (
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-xs uppercase tracking-wide">File PDF (max 10MB ciascuno)</Label>
              <Input type="file" accept=".pdf,application/pdf" multiple
                     onChange={(e) => setFiles(Array.from(e.target.files || []))}
                     data-testid="payslip-files-input" />
              {files.length > 0 && (
                <p className="text-[11px] text-slate-500 mt-1">{files.length} file selezionati</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide">Cartella di destinazione</Label>
                <Select value={folderId} onValueChange={setFolderId}>
                  <SelectTrigger data-testid="payslip-folder-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Nessuna cartella —</SelectItem>
                    {folderOptions.map(f => (
                      <SelectItem key={f.folder_id} value={f.folder_id}>{f.path}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide">Anno</Label>
                <Input type="number" min="2000" max="2100" value={year}
                       onChange={(e) => setYear(e.target.value)} data-testid="payslip-year-input" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose}>Annulla</Button>
              <Button onClick={analyze} disabled={files.length === 0} data-testid="payslip-analyze-btn">
                Analizza {files.length > 0 ? `${files.length} file` : ''}
              </Button>
            </div>
          </div>
        )}

        {busy && (
          <div className="py-10 text-center space-y-3">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
            <p className="text-sm text-slate-600">
              {phase === 'analyzing' ? 'Analisi dei PDF in corso…' : 'Caricamento in corso…'} ({progress}/{files.length})
            </p>
          </div>
        )}

        {phase === 'review' && (
          <div className="space-y-3 py-1">
            <p className="text-sm text-slate-700" data-testid="payslip-review-summary">
              <b>{matchedCount}</b> su <b>{results.length}</b> buste paga riconosciute.
              {results.length - matchedCount > 0 && ' Quelle non riconosciute non verranno caricate: puoi caricarle a mano dal profilo del dipendente.'}
            </p>
            <div className="max-h-72 overflow-y-auto border border-slate-200 divide-y divide-slate-100">
              {results.map(r => (
                <div key={r.filename} className="px-3 py-2 flex items-center gap-2 text-xs" data-testid={`payslip-row-${r.filename}`}>
                  <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <span className="font-medium text-slate-700 truncate flex-1">{r.filename}</span>
                  {r.status === 'matched' ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700">
                      <CheckCircle2 className="w-3.5 h-3.5" /> {r.staff_name}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-rose-700" title={r.reason}>
                      <XCircle className="w-3.5 h-3.5" /> {r.reason}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => { setPhase('select'); setResults([]); }}>Indietro</Button>
              <Button onClick={commit} disabled={matchedCount === 0} data-testid="payslip-commit-btn">
                Carica {matchedCount} riconosciut{matchedCount === 1 ? 'a' : 'e'}
              </Button>
            </div>
          </div>
        )}

        {phase === 'done' && finalSummary && (
          <div className="py-6 text-center space-y-3" data-testid="payslip-done">
            <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500" />
            <p className="text-sm text-slate-700">
              <b>{finalSummary.assigned}</b> buste paga assegnate ai dipendenti
              {finalSummary.failed > 0 && <> · <span className="text-rose-600">{finalSummary.failed} non caricate</span></>}
            </p>
            <Button onClick={onClose} data-testid="payslip-close-btn">Chiudi</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
